/**
 * Toast Schedules — Operations Dashboard
 *
 * Layout (operations-editorial direction):
 *   [KPI strip — hours · shifts · staff · avg shift · next-week delta]
 *   ┌─ Hourly coverage curve (hero) ─────┬─ Position donut ─┐
 *   │                                    │                  │
 *   ├─ Week-over-week dual line ─────────┼─ Top staff rank ─┤
 *   └────────────────────────────────────┴──────────────────┘
 *   [Weekly calendar — drill-in detail at the bottom]
 *
 * Pure inline SVG charts; no chart libraries. Aggregates extend the
 * existing per-day/position/employee rollups with two new shapes:
 *   - hourlyPeakStaff[0..23] — max concurrent staffing per hour
 *   - hoursByNextWeekDay[0..6] — daily hours for the trailing week
 */
import React, { useState, useMemo } from 'react';

// ─── Chart palette ─────────────────────────────────────────────────
const ACCENT = '#ff6b35';
const ACCENT_LIGHT = '#ffb78c';
const POSITION_PALETTE = [
  '#ff6b35', // brand orange
  '#ff8c5a',
  '#2b3a4b', // slate
  '#7d8a99',
  '#ffd1b3',
  '#5b6b7c',
  '#ffa570',
];

const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => {
  if (i === 0) return '12a';
  if (i === 12) return '12p';
  return i < 12 ? `${i}a` : `${i - 12}p`;
});
const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// ─── SVG Charts ────────────────────────────────────────────────────

/**
 * Hero hourly coverage chart — multi-line, one line per position.
 *
 * `series` is `[{ name, color, data: [24] }, ...]` where `data[h]` is the
 * peak concurrent staff in that position at hour `h` across any day in
 * the displayed week. Lines are drawn with light bezier smoothing so the
 * shape reads at a glance without misrepresenting integer staff counts;
 * end-of-line dots anchor the last data point.
 *
 * The series with the highest cumulative peak also gets a translucent
 * area fill underneath — visual weight for the dominant position, but
 * subtle enough that overlapping doesn't muddy the other lines.
 */
const MultiLineChart = ({ series, labels, xTickInterval = 3, height = 220, ariaLabel }) => {
  const allValues = series.flatMap(s => s.data || []);
  const max = Math.max(1, ...allValues);
  const n = labels.length;
  const padLeft = 38;
  const padRight = 16;
  const padTop = 12;
  const padBottom = 28;
  const vbW = 720;
  const chartW = vbW - padLeft - padRight;
  const chartH = height - padTop - padBottom;
  const stepX = chartW / Math.max(1, n - 1);

  const pathFor = (data) => {
    const pts = data.map((v, i) => [
      padLeft + i * stepX,
      padTop + chartH - (v / max) * chartH,
    ]);
    let path = '';
    pts.forEach((p, i) => {
      if (i === 0) {
        path += `M ${p[0]},${p[1]}`;
      } else {
        const prev = pts[i - 1];
        const cpX = (prev[0] + p[0]) / 2;
        path += ` C ${cpX},${prev[1]} ${cpX},${p[1]} ${p[0]},${p[1]}`;
      }
    });
    const areaPath = pts.length > 0
      ? `${path} L ${padLeft + chartW},${padTop + chartH} L ${padLeft},${padTop + chartH} Z`
      : '';
    return { path, areaPath };
  };

  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const heroGradId = `coverage-hero-${Math.random().toString(36).slice(2, 8)}`;

  // Series with the largest total peak gets the area fill underneath.
  const heroIdx = series.reduce((bestIdx, s, i, arr) => {
    const total = (s.data || []).reduce((a, b) => a + b, 0);
    const bestTotal = (arr[bestIdx].data || []).reduce((a, b) => a + b, 0);
    return total > bestTotal ? i : bestIdx;
  }, 0);

  return (
    <svg
      viewBox={`0 0 ${vbW} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={heroGradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={series[heroIdx]?.color || ACCENT} stopOpacity="0.18" />
          <stop offset="100%" stopColor={series[heroIdx]?.color || ACCENT} stopOpacity="0" />
        </linearGradient>
      </defs>
      {yTicks.map((t, i) => {
        const y = padTop + chartH * (1 - t);
        return (
          <g key={i}>
            <line x1={padLeft} x2={vbW - padRight} y1={y} y2={y} stroke="#f1f1f1" strokeWidth="1" />
            <text
              x={padLeft - 8}
              y={y + 3}
              textAnchor="end"
              fontSize="10"
              fill="#a3a3a3"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {Math.round(max * t)}
            </text>
          </g>
        );
      })}
      {series.map((s, i) => {
        const { path, areaPath } = pathFor(s.data || []);
        const isHero = i === heroIdx;
        return (
          <g key={s.guid || i}>
            {isHero && areaPath && <path d={areaPath} fill={`url(#${heroGradId})`} />}
            {path && (
              <path
                d={path}
                stroke={s.color}
                strokeWidth={isHero ? 2.4 : 1.8}
                fill="none"
                strokeLinejoin="round"
                strokeLinecap="round"
                opacity={isHero ? 1 : 0.95}
              />
            )}
            {/* End-cap dot at last data point for anchor */}
            {(s.data && s.data.length > 0) && (() => {
              const lastIdx = s.data.length - 1;
              const x = padLeft + lastIdx * stepX;
              const y = padTop + chartH - (s.data[lastIdx] / max) * chartH;
              return <circle cx={x} cy={y} r="2.5" fill={s.color} stroke="#fff" strokeWidth="1" />;
            })()}
          </g>
        );
      })}
      {labels.map((l, i) => {
        if (i % xTickInterval !== 0 && i !== n - 1) return null;
        const x = padLeft + i * stepX;
        return (
          <text
            key={i}
            x={x}
            y={height - 8}
            textAnchor="middle"
            fontSize="10"
            fill="#9a9a9a"
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          >
            {l}
          </text>
        );
      })}
    </svg>
  );
};

/**
 * Dual line chart for week-over-week comparison. Series A is the
 * "current" week (solid), Series B is "compare" (dashed). Y axis
 * shares a single max so the comparison is honest.
 */
const DualLineChart = ({ seriesA, seriesB, labels, height = 200, ariaLabel }) => {
  const max = Math.max(1, ...seriesA, ...seriesB);
  const n = labels.length;
  const padLeft = 34;
  const padRight = 16;
  const padTop = 16;
  const padBottom = 28;
  const vbW = 520;
  const chartW = vbW - padLeft - padRight;
  const chartH = height - padTop - padBottom;
  const stepX = chartW / Math.max(1, n - 1);

  const pathFor = (data) => data.map((v, i) => {
    const x = padLeft + i * stepX;
    const y = padTop + chartH - (v / max) * chartH;
    return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
  }).join(' ');

  const dotsFor = (data, color) => data.map((v, i) => {
    const x = padLeft + i * stepX;
    const y = padTop + chartH - (v / max) * chartH;
    return <circle key={i} cx={x} cy={y} r="3" fill={color} stroke="#fff" strokeWidth="1.5" />;
  });

  return (
    <svg
      viewBox={`0 0 ${vbW} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
    >
      {[0, 0.5, 1].map((t, i) => {
        const y = padTop + chartH * (1 - t);
        return (
          <g key={i}>
            <line x1={padLeft} x2={vbW - padRight} y1={y} y2={y} stroke="#f1f1f1" strokeWidth="1" />
            <text
              x={padLeft - 6}
              y={y + 3}
              textAnchor="end"
              fontSize="10"
              fill="#a3a3a3"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {Math.round(max * t)}
            </text>
          </g>
        );
      })}
      <path d={pathFor(seriesB)} stroke={ACCENT_LIGHT} strokeWidth="1.5" strokeDasharray="4 4" fill="none" />
      <path d={pathFor(seriesA)} stroke={ACCENT} strokeWidth="2.2" fill="none" strokeLinejoin="round" strokeLinecap="round" />
      {dotsFor(seriesB, ACCENT_LIGHT)}
      {dotsFor(seriesA, ACCENT)}
      {labels.map((l, i) => {
        const x = padLeft + i * stepX;
        return (
          <text
            key={i}
            x={x}
            y={height - 8}
            textAnchor="middle"
            fontSize="10"
            fill="#9a9a9a"
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          >
            {l}
          </text>
        );
      })}
    </svg>
  );
};

/**
 * Donut for position share. Slice colors come from POSITION_PALETTE
 * by index; center reads the total. Rendered with stroke-dasharray on
 * concentric circles — simple, no path math.
 */
const Donut = ({ slices, size = 160, thickness = 18, centerValue, centerLabel }) => {
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const total = slices.reduce((s, x) => s + x.value, 0);
  let offset = 0;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label="Hours by position">
      <circle cx={c} cy={c} r={r} fill="none" stroke="#f3f3f3" strokeWidth={thickness} />
      {slices.map((slice, i) => {
        const pct = total > 0 ? slice.value / total : 0;
        const length = pct * circumference;
        const dash = `${length} ${circumference - length}`;
        const dashOffset = -offset;
        offset += length;
        return (
          <circle
            key={i}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={slice.color}
            strokeWidth={thickness}
            strokeDasharray={dash}
            strokeDashoffset={dashOffset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${c} ${c})`}
          />
        );
      })}
      <text
        x={c}
        y={c - 2}
        textAnchor="middle"
        fontSize="26"
        fontWeight="700"
        fill="#1a1a1a"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
      >
        {centerValue}
      </text>
      <text
        x={c}
        y={c + 16}
        textAnchor="middle"
        fontSize="9"
        fill="#9a9a9a"
        letterSpacing="2"
      >
        {centerLabel}
      </text>
    </svg>
  );
};

// ─── Main component ────────────────────────────────────────────────

const ToastSchedulesView = ({ shifts, employees, jobs, loading }) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');

  const getEmployeeName = (employeeRef) => {
    if (!employeeRef) return 'Unassigned';
    const emp = employees.find(e => e.guid === employeeRef.guid);
    return emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : 'Unknown';
  };

  const getJobName = (jobRef) => {
    if (!jobRef) return 'Unknown';
    const job = jobs.find(j => j.guid === jobRef.guid);
    return job?.title || job?.name || 'Unknown';
  };

  // Memoized week range — keep downstream useMemos honest.
  const weekDates = useMemo(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + (weekOffset * 7));
    startOfWeek.setHours(0, 0, 0, 0);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [weekOffset]);

  const filteredShifts = useMemo(() => shifts.filter(s => {
    if (employeeFilter && s.employeeReference?.guid !== employeeFilter) return false;
    if (jobFilter && s.jobReference?.guid !== jobFilter) return false;
    return true;
  }), [shifts, employeeFilter, jobFilter]);

  const aggregates = useMemo(() => {
    const thisWeekStart = weekDates[0].getTime();
    const thisWeekEndDate = new Date(weekDates[6]);
    thisWeekEndDate.setHours(23, 59, 59, 999);
    const thisWeekEnd = thisWeekEndDate.getTime();
    const nextWeekStart = thisWeekEnd + 1;
    const nextWeekEnd = nextWeekStart + 7 * 24 * 60 * 60 * 1000;

    let thisWeekHours = 0;
    let nextWeekHours = 0;
    let thisWeekShiftCount = 0;
    let nextWeekShiftCount = 0;
    const thisWeekStaff = new Set();
    const hoursByDay = weekDates.map(d => ({ date: d, hours: 0, shifts: 0, people: new Set() }));
    const hoursByNextWeekDay = new Array(7).fill(0);
    const hoursByJob = new Map();
    const hoursByEmployee = new Map();
    // 7×24 staffing grid for the displayed week — used to derive
    // hourlyPeakStaff[hour] = max concurrent shifts at that hour across
    // any day of the week.
    const dayHourCount = Array.from({ length: 7 }, () => new Array(24).fill(0));
    // Per-position 7×24 grids — same shape, but split by jobGuid so we
    // can plot one line per position in the hourly coverage chart.
    const jobHourCount = new Map();

    for (const shift of filteredShifts) {
      if (!shift.inDate || !shift.outDate) continue;
      const start = new Date(shift.inDate);
      const end = new Date(shift.outDate);
      const ms = end.getTime() - start.getTime();
      if (!Number.isFinite(ms) || ms <= 0) continue;
      const hours = ms / (1000 * 60 * 60);
      const ts = start.getTime();

      if (ts >= thisWeekStart && ts <= thisWeekEnd) {
        thisWeekHours += hours;
        thisWeekShiftCount += 1;
        const empGuid = shift.employeeReference?.guid;
        if (empGuid) thisWeekStaff.add(empGuid);

        const dayIdx = Math.floor((ts - thisWeekStart) / (24 * 60 * 60 * 1000));
        if (hoursByDay[dayIdx]) {
          hoursByDay[dayIdx].hours += hours;
          hoursByDay[dayIdx].shifts += 1;
          if (empGuid) hoursByDay[dayIdx].people.add(empGuid);
        }

        const jobGuid = shift.jobReference?.guid;

        // Hour-of-day staffing for the line chart — both the overall
        // peak (dayHourCount) and the per-position breakdown.
        if (dayIdx >= 0 && dayIdx < 7) {
          const startHour = start.getHours();
          const endHour = end.getHours() + (end.getMinutes() > 0 ? 1 : 0);
          let jobGrid = null;
          if (jobGuid) {
            if (!jobHourCount.has(jobGuid)) {
              jobHourCount.set(jobGuid, Array.from({ length: 7 }, () => new Array(24).fill(0)));
            }
            jobGrid = jobHourCount.get(jobGuid);
          }
          for (let h = startHour; h < Math.min(endHour, 24); h++) {
            dayHourCount[dayIdx][h] += 1;
            if (jobGrid) jobGrid[dayIdx][h] += 1;
          }
        }

        if (jobGuid) {
          const cur = hoursByJob.get(jobGuid) || { hours: 0, shifts: 0 };
          hoursByJob.set(jobGuid, { hours: cur.hours + hours, shifts: cur.shifts + 1 });
        }
        if (empGuid) {
          const cur = hoursByEmployee.get(empGuid) || { hours: 0, shifts: 0 };
          hoursByEmployee.set(empGuid, { hours: cur.hours + hours, shifts: cur.shifts + 1 });
        }
      } else if (ts >= nextWeekStart && ts <= nextWeekEnd) {
        nextWeekHours += hours;
        nextWeekShiftCount += 1;
        const dayIdx = Math.floor((ts - nextWeekStart) / (24 * 60 * 60 * 1000));
        if (dayIdx >= 0 && dayIdx < 7) hoursByNextWeekDay[dayIdx] += hours;
      }
    }

    const hourlyPeakStaff = new Array(24).fill(0);
    for (let h = 0; h < 24; h++) {
      let peak = 0;
      for (let d = 0; d < 7; d++) {
        if (dayHourCount[d][h] > peak) peak = dayHourCount[d][h];
      }
      hourlyPeakStaff[h] = peak;
    }

    const positionBreakdown = Array.from(hoursByJob.entries())
      .map(([guid, data], idx) => {
        const job = jobs.find(j => j.guid === guid);
        return {
          guid,
          name: job?.title || job?.name || 'Unknown',
          hours: data.hours,
          shifts: data.shifts,
          pct: thisWeekHours > 0 ? (data.hours / thisWeekHours) * 100 : 0,
          color: POSITION_PALETTE[idx % POSITION_PALETTE.length],
        };
      })
      .sort((a, b) => b.hours - a.hours)
      // Re-assign palette after sorting so the largest slice gets the
      // brand orange.
      .map((p, i) => ({ ...p, color: POSITION_PALETTE[i % POSITION_PALETTE.length] }));

    // Per-position peak staffing curve for the hero chart. One series
    // per position; cap at top 6 by total scheduled hours so the chart
    // doesn't turn into spaghetti for venues with many job titles.
    // Colors are pulled from positionBreakdown so the chart's legend
    // matches the donut exactly.
    const colorByJob = new Map(positionBreakdown.map(p => [p.guid, p.color]));
    const hourlyByPosition = Array.from(jobHourCount.entries())
      .map(([guid, grid]) => {
        const peak = new Array(24).fill(0);
        for (let h = 0; h < 24; h++) {
          let p = 0;
          for (let d = 0; d < 7; d++) {
            if (grid[d][h] > p) p = grid[d][h];
          }
          peak[h] = p;
        }
        const totalPeak = peak.reduce((a, b) => a + b, 0);
        const job = jobs.find(j => j.guid === guid);
        return {
          guid,
          name: job?.title || job?.name || 'Unknown',
          data: peak,
          color: colorByJob.get(guid) || POSITION_PALETTE[0],
          totalPeak,
          maxPeak: Math.max(...peak),
        };
      })
      .sort((a, b) => b.totalPeak - a.totalPeak)
      .slice(0, 6);

    const topEmployees = Array.from(hoursByEmployee.entries())
      .map(([guid, data]) => {
        const emp = employees.find(e => e.guid === guid);
        return {
          guid,
          name: emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : 'Unknown',
          hours: data.hours,
          shifts: data.shifts,
        };
      })
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 5);

    const avgShiftHours = thisWeekShiftCount > 0 ? thisWeekHours / thisWeekShiftCount : 0;
    const hoursDelta = nextWeekHours - thisWeekHours;
    const hoursPctChange = thisWeekHours > 0 ? (hoursDelta / thisWeekHours) * 100 : null;

    return {
      thisWeekHours,
      thisWeekShiftCount,
      uniqueStaffCount: thisWeekStaff.size,
      avgShiftHours,
      hoursByDay,
      hoursByNextWeekDay,
      hourlyPeakStaff,
      hourlyByPosition,
      positionBreakdown,
      topEmployees,
      nextWeekHours,
      nextWeekShiftCount,
      hoursDelta,
      hoursPctChange,
    };
  }, [filteredShifts, weekDates, jobs, employees]);

  const shiftsByDate = useMemo(() => {
    const organized = {};
    weekDates.forEach(date => { organized[date.toISOString().split('T')[0]] = []; });
    filteredShifts.forEach(shift => {
      if (!shift.inDate) return;
      const dateKey = new Date(shift.inDate).toISOString().split('T')[0];
      if (organized[dateKey]) organized[dateKey].push(shift);
    });
    Object.keys(organized).forEach(k => {
      organized[k].sort((a, b) => new Date(a.inDate) - new Date(b.inDate));
    });
    return organized;
  }, [filteredShifts, weekDates]);

  const formatTime = (dateString) => {
    if (!dateString) return '--:--';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const formatHours = (h) => {
    if (!Number.isFinite(h)) return '0';
    if (h < 10) return h.toFixed(1);
    return String(Math.round(h));
  };

  const formatWeekHeader = () => {
    const start = weekDates[0];
    const end = weekDates[6];
    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const year = start.getFullYear();
    if (startMonth === endMonth) return `${startMonth} ${start.getDate()} – ${end.getDate()}, ${year}`;
    return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}, ${year}`;
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const activeEmployees = employees.filter(e => !e.deleted && !e.disabled);

  const pctChange = aggregates.hoursPctChange;
  const deltaClass = pctChange === null
    ? 'is-neutral'
    : pctChange > 0 ? 'is-up' : pctChange < 0 ? 'is-down' : 'is-neutral';
  const deltaArrow = pctChange === null ? '—' : pctChange > 0 ? '↗' : pctChange < 0 ? '↘' : '→';

  const hasNextWeek = aggregates.nextWeekHours > 0 || aggregates.nextWeekShiftCount > 0;
  const seriesA = aggregates.hoursByDay.map(d => d.hours);
  const seriesB = aggregates.hoursByNextWeekDay;

  return (
    <div className="toast-list-container sched-dashboard">
      {/* Toolbar */}
      <div className="list-toolbar sched-toolbar">
        <div className="week-nav">
          <button className="week-nav-btn" onClick={() => setWeekOffset(w => w - 1)} aria-label="Previous week">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <span className="week-label sched-week-label">{formatWeekHeader()}</span>
          <button className="week-nav-btn" onClick={() => setWeekOffset(w => w + 1)} aria-label="Next week">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          {weekOffset !== 0 && (
            <button className="today-btn" onClick={() => setWeekOffset(0)}>Today</button>
          )}
        </div>

        <select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} className="filter-select">
          <option value="">All Employees</option>
          {activeEmployees.map(emp => (
            <option key={emp.guid} value={emp.guid}>{emp.firstName} {emp.lastName}</option>
          ))}
        </select>

        <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} className="filter-select">
          <option value="">All Positions</option>
          {jobs.map(job => (
            <option key={job.guid} value={job.guid}>{job.title || job.name}</option>
          ))}
        </select>

        <span className="results-count">
          {aggregates.thisWeekShiftCount} shifts · {formatHours(aggregates.thisWeekHours)}h
        </span>
      </div>

      {/* KPI strip */}
      <section className="sched-kpis" aria-label="Weekly summary">
        <article className="sched-kpi sched-kpi--hero">
          <span className="sched-kpi-eyebrow">Week of {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          <span className="sched-kpi-figure">
            <strong>{formatHours(aggregates.thisWeekHours)}</strong>
            <span className="sched-kpi-unit">hrs</span>
          </span>
          <span className="sched-kpi-label">Scheduled</span>
        </article>
        <article className="sched-kpi">
          <span className="sched-kpi-figure"><strong>{aggregates.thisWeekShiftCount}</strong></span>
          <span className="sched-kpi-label">Shifts</span>
        </article>
        <article className="sched-kpi">
          <span className="sched-kpi-figure"><strong>{aggregates.uniqueStaffCount}</strong></span>
          <span className="sched-kpi-label">Distinct staff</span>
        </article>
        <article className="sched-kpi">
          <span className="sched-kpi-figure">
            <strong>{formatHours(aggregates.avgShiftHours)}</strong>
            <span className="sched-kpi-unit">hrs</span>
          </span>
          <span className="sched-kpi-label">Avg shift</span>
        </article>
        {hasNextWeek && (
          <article className={`sched-kpi sched-kpi--delta ${deltaClass}`}>
            <span className="sched-kpi-figure">
              <strong>{pctChange === null ? '—' : `${pctChange > 0 ? '+' : ''}${Math.round(pctChange)}%`}</strong>
              <span className="sched-kpi-arrow" aria-hidden="true">{deltaArrow}</span>
            </span>
            <span className="sched-kpi-label">Next week vs this</span>
          </article>
        )}
      </section>

      {/* Main 2×2 dashboard grid */}
      <section className="sched-grid">
        <article className="sched-card sched-card--coverage">
          <header className="sched-card-head">
            <div>
              <h3 className="sched-card-title">Hourly coverage by position</h3>
              <p className="sched-card-sub">
                Peak staff scheduled at each hour, split by role
              </p>
            </div>
            <div className="sched-card-stat">
              <span className="sched-card-stat-value">
                {Math.max(0, ...aggregates.hourlyPeakStaff)}
              </span>
              <span className="sched-card-stat-label">total peak</span>
            </div>
          </header>
          <div className="sched-card-chart">
            {aggregates.hourlyByPosition.length === 0 ? (
              <div className="sched-card-empty">No staffing data this week.</div>
            ) : (
              <MultiLineChart
                series={aggregates.hourlyByPosition}
                labels={HOUR_LABELS}
                ariaLabel={`Peak concurrent staffing by hour, split across ${aggregates.hourlyByPosition.length} positions`}
              />
            )}
          </div>
          {aggregates.hourlyByPosition.length > 0 && (
            <ul className="sched-line-legend" aria-label="Position legend">
              {aggregates.hourlyByPosition.map(s => (
                <li key={s.guid}>
                  <span className="sched-line-legend-swatch" style={{ background: s.color }} aria-hidden="true" />
                  <span className="sched-line-legend-name">{s.name}</span>
                  <span className="sched-line-legend-peak">
                    <strong>{s.maxPeak}</strong>
                    <span className="u"> peak</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="sched-card sched-card--positions">
          <header className="sched-card-head">
            <div>
              <h3 className="sched-card-title">By position</h3>
              <p className="sched-card-sub">{aggregates.positionBreakdown.length} positions</p>
            </div>
          </header>
          {aggregates.positionBreakdown.length === 0 ? (
            <div className="sched-card-empty">No position data this week.</div>
          ) : (
            <div className="sched-donut-row">
              <Donut
                slices={aggregates.positionBreakdown.map(p => ({ value: p.hours, color: p.color }))}
                centerValue={Math.round(aggregates.thisWeekHours)}
                centerLabel="HOURS"
              />
              <ul className="sched-donut-legend">
                {aggregates.positionBreakdown.map(p => (
                  <li key={p.guid}>
                    <span className="sched-legend-dot" style={{ background: p.color }} aria-hidden="true" />
                    <span className="sched-legend-name">{p.name}</span>
                    <span className="sched-legend-value">{Math.round(p.hours)}<span className="u">h</span></span>
                    <span className="sched-legend-pct">{p.pct.toFixed(0)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </article>

        <article className="sched-card sched-card--trend">
          <header className="sched-card-head">
            <div>
              <h3 className="sched-card-title">Week over week</h3>
              <p className="sched-card-sub">
                <span className="sched-legend-key"><span className="sched-line-swatch is-solid" /> this week</span>
                <span className="sched-legend-key"><span className="sched-line-swatch is-dashed" /> next week</span>
              </p>
            </div>
            {hasNextWeek && (
              <div className={`sched-card-stat ${deltaClass}`}>
                <span className="sched-card-stat-value">
                  {pctChange === null ? '—' : `${pctChange > 0 ? '+' : ''}${Math.round(pctChange)}%`}
                </span>
                <span className="sched-card-stat-label">delta</span>
              </div>
            )}
          </header>
          <div className="sched-card-chart">
            <DualLineChart
              seriesA={seriesA}
              seriesB={seriesB}
              labels={DAY_LABELS}
              ariaLabel="Daily hours, this week versus next"
            />
          </div>
        </article>

        <article className="sched-card sched-card--rank">
          <header className="sched-card-head">
            <div>
              <h3 className="sched-card-title">Most scheduled</h3>
              <p className="sched-card-sub">{employeeFilter ? 'Filtered to one' : `Top ${aggregates.topEmployees.length}`}</p>
            </div>
          </header>
          {aggregates.topEmployees.length === 0 ? (
            <div className="sched-card-empty">No staff scheduled.</div>
          ) : (
            <ol className="sched-rank-list">
              {aggregates.topEmployees.map((emp, i) => {
                const maxHours = aggregates.topEmployees[0]?.hours || 1;
                const pct = (emp.hours / maxHours) * 100;
                return (
                  <li key={emp.guid} className="sched-rank-row">
                    <span className="sched-rank-pos">{String(i + 1).padStart(2, '0')}</span>
                    <span className="sched-rank-name">{emp.name}</span>
                    <span className="sched-rank-bar" aria-hidden="true">
                      <span className="sched-rank-bar-fill" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="sched-rank-hours">
                      <strong>{Math.round(emp.hours)}</strong>
                      <span className="u">h</span>
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </article>
      </section>

      {/* Calendar — drill-in detail */}
      <section className="sched-card sched-card--calendar">
        <header className="sched-card-head">
          <div>
            <h3 className="sched-card-title">Weekly calendar</h3>
            <p className="sched-card-sub">Individual shifts; tap an entry to inspect</p>
          </div>
        </header>
        <div className="schedule-calendar">
          <div className="calendar-header">
            {weekDates.map(date => (
              <div key={date.toISOString()} className={`calendar-day-header ${isToday(date) ? 'today' : ''}`}>
                <span className="day-name">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                <span className="day-number">{date.getDate()}</span>
              </div>
            ))}
          </div>
          <div className="calendar-body">
            {weekDates.map(date => {
              const dateKey = date.toISOString().split('T')[0];
              const dayShifts = shiftsByDate[dateKey] || [];
              return (
                <div key={date.toISOString()} className={`calendar-day ${isToday(date) ? 'today' : ''}`}>
                  {dayShifts.length === 0 ? (
                    <div className="no-shifts">No shifts</div>
                  ) : (
                    dayShifts.map(shift => (
                      <div key={shift.guid} className="shift-card">
                        <div className="shift-time">
                          {formatTime(shift.inDate)} – {formatTime(shift.outDate)}
                        </div>
                        <div className="shift-employee">{getEmployeeName(shift.employeeReference)}</div>
                        <div className="shift-job">{getJobName(shift.jobReference)}</div>
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {shifts.length === 0 && !loading && (
        <div className="empty-state">
          <h3>No schedules found</h3>
          <p>Shift schedules from Toast will appear here once they're created</p>
        </div>
      )}
    </div>
  );
};

export default ToastSchedulesView;
